import { useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { formatHskLevels, formatSessionWhen, missedCardsFrom, sessionScoreParts } from "../helpers/progress";
import { SavedTestResult } from "../helpers/storage";
import { menuColors } from "../theme/menuTheme";
import CompactCardGrid from "./CompactCardGrid";

type ResultFilter = "right" | "wrong" | "unanswered";

const cardMatchesFilter = (answer: boolean | null | undefined, filters: Set<ResultFilter>) => {
  if (filters.size === 0) {
    return true;
  }
  if (answer === true) {
    return filters.has("right");
  }
  if (answer === false) {
    return filters.has("wrong");
  }
  return filters.has("unanswered");
};

const TestSessionDetail = ({
  result,
  onBack,
  onRetryMissed,
  onRetryTest
}: {
  result: SavedTestResult;
  onBack: () => void;
  onRetryMissed?: (cards: SavedTestResult["cards"]) => void;
  onRetryTest?: (cards: SavedTestResult["cards"]) => void;
}) => {
  const [showAllTiles, setShowAllTiles] = useState(false);
  const [columnCount, setColumnCount] = useState(4);
  const [filters, setFilters] = useState<Set<ResultFilter>>(new Set());
  const answersByOrder = Object.fromEntries(result.answers.map((entry) => [entry.order, entry.answer]));
  const { right, wrong, unanswered, pct } = sessionScoreParts(result.cards, result.answers);
  const missed = missedCardsFrom(result.cards, result.answers);
  const visibleCards = result.cards.filter((card) => cardMatchesFilter(answersByOrder[card.order], filters));
  const hiddenCount = showAllTiles ? 0 : Math.max(0, visibleCards.length - columnCount * 3);
  const title = result.finishedAt ? formatSessionWhen(result.finishedAt) : "Last test";
  const levels = formatHskLevels(result.filteredLevels ?? []);

  const toggleFilter = (filter: ResultFilter) => {
    setShowAllTiles(false);
    setFilters((current) => {
      const next = new Set(current);
      if (next.has(filter)) {
        next.delete(filter);
      } else {
        next.add(filter);
      }
      return next;
    });
  };

  const statButtonSx = (selected: boolean, accent: string) => ({
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: 0.25,
    py: 1,
    px: 0.5,
    minHeight: 64,
    borderRadius: 1,
    border: "1px solid",
    borderColor: selected ? accent : menuColors.border,
    backgroundColor: selected ? `${accent}22` : menuColors.surfaceRaised,
    boxShadow: selected ? `inset 0 0 0 1px ${accent}` : "none",
    cursor: "pointer",
    textTransform: "none" as const
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, pt: 1, flexShrink: 0 }}>
        <IconButton aria-label="Back" onClick={onBack} sx={{ color: menuColors.text, ml: -0.5 }}>
          <ChevronLeftIcon />
        </IconButton>
        <Box>
          <Typography sx={{ fontWeight: 600, color: menuColors.text, fontSize: 18, lineHeight: 1.2 }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: menuColors.textMuted }}>
            {levels}
            {result.cards.length ? ` · ${result.cards.length} cards` : ""}
            {result.shuffled ? " · shuffled" : ""}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 2, pt: 1, pb: 2 }}>
        <Typography sx={{ fontSize: 28, fontWeight: 700, color: menuColors.accent, lineHeight: 1.1, mb: 1.5 }}>
          {pct}%
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 1,
            mb: 1.5
          }}>
          <Button
            aria-pressed={filters.has("right")}
            onClick={() => toggleFilter("right")}
            sx={statButtonSx(filters.has("right"), "#5DCAA5")}>
            <Typography sx={{ fontSize: 11, color: menuColors.textMuted, fontWeight: 600 }}>
              Right
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#5DCAA5", lineHeight: 1.1 }}>
              {right}
            </Typography>
          </Button>
          <Button
            aria-pressed={filters.has("wrong")}
            onClick={() => toggleFilter("wrong")}
            sx={statButtonSx(filters.has("wrong"), "#F0997B")}>
            <Typography sx={{ fontSize: 11, color: menuColors.textMuted, fontWeight: 600 }}>
              Wrong
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#F0997B", lineHeight: 1.1 }}>
              {wrong}
            </Typography>
          </Button>
          <Button
            aria-pressed={filters.has("unanswered")}
            onClick={() => toggleFilter("unanswered")}
            sx={statButtonSx(filters.has("unanswered"), "#94a3b8")}>
            <Typography sx={{ fontSize: 11, color: menuColors.textMuted, fontWeight: 600 }}>
              Unanswered
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: menuColors.textMuted, lineHeight: 1.1 }}>
              {unanswered}
            </Typography>
          </Button>
        </Box>
        {visibleCards.length > 0 ? (
          <CompactCardGrid
            cards={visibleCards}
            isMobile
            answersByOrder={answersByOrder}
            readOnly
            maxRows={showAllTiles ? undefined : 3}
            onColumnCountChange={setColumnCount}
          />
        ) : (
          <Typography variant="body2" sx={{ color: menuColors.textMuted, textAlign: "center", mb: 0 }}>
            No cards match those filters.
          </Typography>
        )}
        {visibleCards.length > 0 && (hiddenCount > 0 || showAllTiles) ? (
          <Button
            fullWidth
            variant="text"
            onClick={() => setShowAllTiles((open) => !open)}
            sx={{ mt: 1, textTransform: "none", fontWeight: 600 }}>
            {showAllTiles ? "Show 3 rows" : `Show ${hiddenCount} more`}
          </Button>
        ) : null}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
          {missed.length > 0 && onRetryMissed ? (
            <Button
              fullWidth
              variant="contained"
              onClick={() => onRetryMissed(missed)}
              sx={{ textTransform: "none", fontWeight: 600 }}>
              Retry missed cards
            </Button>
          ) : null}
          {onRetryTest ? (
            <Button
              fullWidth
              variant="outlined"
              onClick={() => onRetryTest(result.cards)}
              sx={{ textTransform: "none", fontWeight: 600 }}>
              Retry test
            </Button>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default TestSessionDetail;
